<template>
  <section class="faq-accordion" ref="root">
    <div class="faq-accordion__inner" :class="{ 'faq-accordion__inner_visible': inView }">
      <h2 class="faq-accordion__headline">Описание</h2>
      <p v-if="error" class="faq-accordion__error">{{ error }}</p>
      <div v-else-if="loading && !items.length" class="faq-accordion__loading">
        <Loader size="lg" />
      </div>
      <div v-else class="faq-accordion__grid">
        <div
          v-for="(item, i) in items"
          :key="i"
          class="faq-accordion__tile"
          :class="{ 'faq-accordion__tile_open': openIndex === i }"
          :style="{ transitionDelay: inView ? `${0.05 + i * 0.06}s` : '0s' }"
        >
          <button
            type="button"
            class="faq-accordion__trigger"
            :aria-expanded="openIndex === i"
            :aria-controls="'faq-body-' + i"
            :id="'faq-trigger-' + i"
            @click="toggle(i)"
          >
            <span class="faq-accordion__title">{{ item.title }}</span>
            <span class="faq-accordion__chevron" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </span>
          </button>
          <div
            :id="'faq-body-' + i"
            class="faq-accordion__body"
            :class="{ 'faq-accordion__body_open': openIndex === i }"
            role="region"
            :aria-labelledby="'faq-trigger-' + i"
          >
            <div class="faq-accordion__content">
              <template v-if="item.list">
                <p v-if="item.lead" class="faq-accordion__lead">{{ item.lead }}</p>
                <ul class="faq-accordion__list">
                  <li v-for="(line, j) in item.list" :key="j">{{ line }}</li>
                </ul>
                <p v-if="item.tail" class="faq-accordion__tail">{{ item.tail }}</p>
              </template>
              <template v-else>
                <p v-for="(p, j) in item.paragraphs" :key="j" class="faq-accordion__p">{{ p }}</p>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import Loader from '@/components/Loader.vue'

export default {
  name: 'FaqAccordion',
  components: { Loader },
  setup() {
    const store = useStore()
    const root = ref(null)
    const inView = ref(false)
    const openIndex = ref(null)
    let observer = null

    const items = computed(() => store.getters['faq/items'] || [])
    const loading = computed(() => store.getters['faq/loading'])
    const error = computed(() => store.getters['faq/error'])

    function toggle(i) {
      openIndex.value = openIndex.value === i ? null : i
    }

    onMounted(() => {
      store.dispatch('faq/fetchFaq')
      observer = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) inView.value = true },
        { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
      )
      if (root.value) observer.observe(root.value)
    })
    onUnmounted(() => observer?.disconnect())

    return { root, inView, openIndex, items, loading, error, toggle }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/_variables.scss";

$dur: 0.5s;
$ease: cubic-bezier(0.22, 1, 0.36, 1);

.faq-accordion {
  padding: 80px $gap 100px;
  background: $color-bg;

  @media (max-width: $breakpoint-tablet) {
    padding: 64px $gap 80px;
  }
  @media (max-width: $breakpoint-mobile) {
    padding: 48px $gap 64px;
  }

  &__inner {
    max-width: $container-max;
    margin: 0 auto;
  }

  &__headline {
    font-size: clamp(12px, 1.2vw, 14px);
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: $color-text-muted;
    margin: 0 0 40px;
    opacity: 0;
    transform: translateY(12px);
    transition: opacity $dur $ease, transform $dur $ease;

    .faq-accordion__inner_visible & {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 180px;
    padding: 40px;
  }

  &__error {
    margin: 0 0 24px;
    font-size: 15px;
    color: #c53030;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    align-items: start;
  }

  &__tile {
    background: $color-primary-inverse;
    border: 1px solid $color-border;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
    opacity: 0;
    transform: translateY(16px);
    transition:
      opacity $dur $ease,
      transform $dur $ease,
      border-color $dur $ease,
      box-shadow $dur $ease;

    .faq-accordion__inner_visible & {
      opacity: 1;
      transform: translateY(0);
    }

    &_open {
      border-color: $color-accent-teal;
      box-shadow: 0 8px 28px rgba(13, 148, 136, 0.12);

      .faq-accordion__chevron {
        transform: rotate(180deg);
        color: $color-accent-teal;
      }
    }
  }

  &__trigger {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 24px;
    text-align: left;
    font: inherit;
    color: $color-text;
    background: none;
    border: none;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.02);
    }
    &:focus-visible {
      outline: 2px solid $color-accent-teal;
      outline-offset: 2px;
    }
  }

  &__title {
    font-size: clamp(15px, 1.4vw, 17px);
    font-weight: 600;
    line-height: 1.35;
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__chevron {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    color: $color-text-muted;
    transition: transform $dur $ease, color $dur $ease;

    svg {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__body {
    max-height: 0;
    overflow: hidden;
    transition: max-height $dur $ease;

    &_open {
      max-height: 520px;
    }
  }

  &__content {
    padding: 15px 24px 20px 24px;
    border-top: 1px solid $color-border;
  }

  &__lead,
  &__p,
  &__tail {
    margin: 0 0 12px;
    font-size: 15px;
    line-height: 1.55;
    color: $color-text-muted;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__lead {
    margin-top: 0;
  }

  &__tail {
    margin-top: 12px;
  }

  &__list {
    margin: 8px 0 0;
    padding-left: 20px;
    font-size: 15px;
    line-height: 1.6;
    color: $color-text-muted;

    li {
      margin-bottom: 6px;
    }
    li:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
